import React, {
    FC, createContext, useContext, useState, ReactNode, MouseEvent, useEffect, useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
    PageHeader, Space, Table, Form, Button, Typography, ConfigProvider, Input
} from 'antd'
import { SearchOutlined, ClockCircleOutlined } from '@ant-design/icons'
import type { PaginationProps } from 'antd'
import moment from 'moment'
import zhCN from 'antd/es/locale/zh_CN'
import { useTranslation } from 'react-i18next'
import type { FormInstance } from 'antd/es/form'
import type { FilterValue } from 'antd/es/table/interface'
import type { IPagination, ILogResData } from '@/store/models/tableEdit.model'
import { getWorkgroup } from '@/store/actions/tableEdit.action'
import { viewHistory } from '@/services/table.service'
import { IAppState } from '@/store/reducers'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getDicClass } from '@/pages/charts'
import { getUuid } from '@/utils/util'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import LogViewDetailPop from '@/pages/table2Mana/components/logViewDetailPop'
import Nav from '@/components/nav'

interface EditableRowProps {
    index: number
}
interface EditableCellProps {
    title: ReactNode
    editable: boolean
    children: ReactNode
    dataIndex: string
    record: any
}
interface childrenType {
    title: string
    dataIndex: string
    key: string
    editable?: boolean
    className?: string
    width?: number
    ellipsis: { showTitle: boolean },
    filters?: {
        text: string
        value: number
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string, record: any) => boolean
    filteredValue?: FilterValue | null
    render?: any
}
interface IColProperty {
    title?: ReactNode | string
    className?: string
    editable?: boolean
    dataIndex?: string | string[]
    children?: childrenType[]
    filters?: {
        text: string
        value: string
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string | number | boolean, record: any) => boolean
}
type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

export const wrapText = (text: string): ReactNode => {
    const br = <br/>
    const empty = <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
    let result
    const contentStr = text ? text.split(';') : ''
    if(contentStr.length < 2) {
        return text
    }
    for(let j = 0; j < contentStr.length; j++) {
        if (j == 0) {
            result = contentStr[j]
        } else if (contentStr[j].startsWith(' ')) {
            result = <span>{result}{br}{empty}{contentStr[j]}</span>
        } else {
            result = <span>{result}{br}{contentStr[j]}</span>
        }
    }
    return <div>{result}</div>
}

const EditableContext = createContext<FormInstance<any> | null>(null)
const DataSourceContext = createContext<{
    dataSource: ILogResData[]
    setDataSource: any
} | null>(null)

const EditableRow: FC<EditableRowProps> = ({ ...props }) => {
    const [form] = Form.useForm()
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}
const EditableCell: FC<EditableCellProps> = ({
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    title,
    children,
    dataIndex,
    record,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false)
    const form = useContext(EditableContext)
  
    const toggleEdit = (value?: string | MouseEvent<HTMLDivElement>): void => {
        if (!record) return
        setEditing(!editing)
        // 修改 form 值为 value
        form?.setFieldsValue({ [dataIndex]: value || record[dataIndex] })
    }

    const save = () => {
        try {
            toggleEdit()
        } catch (errInfo: any) {
            console.log('Save failed:', errInfo.message)
        }
    }
  
    let childNode = children
    childNode = (
        <div
            style={{ paddingRight: 24 }}
            onClick={toggleEdit}
        >
            { children }
        </div>
    )

    return <td {...restProps} onClick={save}>{ childNode }</td>
}

const TablePage2: FC = () => {
    const dispatch = useDispatch()
    const history: any = useHistory()
    const locationObj = useLocation<any>()
    const { t, i18n } = useTranslation()
    const route = useSelector((state: IAppState) => state.router)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    
    const { Text } = Typography
    const [dataSource, setDataSource] = useState<ILogResData[]>([])
    const [init, setInit] = useState(true)
    const [winH, setWinh] = useState<number>(0)
    const [showLogDetailPop, setShowLogDetailPop] = useState(false)
    // 列内容搜索
    const [paginationInfo, setPaginationInfo] = useState<IPagination>({
        pageIndex: 1,
        pageSize: 10,
        total: 0
    })
    const [tableLoading, setTableLoading] = useState(true)

    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)
    const didMountRef = useRef(false)

    // 搜索
    const [searchObj, setSearchObj] = useState<any>({
        table: 'tech_project_info',
        techPtn: ''
    })
    const [tableKey, setTableKey] = useState('0')
    const [viewHistoryId, setViewHistoryId] = useState<number | null>(null)
    const [viewHistoryRecord, setViewHistoryRecord] = useState<any>(null)

    const viewButton = (text: string, record: any) => {
        return authInfo ? (
            <Space>
                {Reflect.has(authInfo, 'viewHistoryProjectInfo') && <Text className='txt_blue hand' title={t('viewAuditChange')}
                    onClick={() => {
                        console.log('record: ', record)
                        setViewHistoryId(record.id)
                        setViewHistoryRecord(record)
                        setShowLogDetailPop(true)
                    }}
                ><ClockCircleOutlined style={{ fontSize: 16 }} /></Text>}
            </Space>
        ) : <></>
    }
    
    const getList = async (params?: IPagination) => {
        try {
            setTableLoading(true)
            const res = await viewHistory({
                ...searchObj,
                pageNo: params?.pageIndex || paginationInfo.pageIndex,
                pageSize: params?.pageSize || paginationInfo.pageSize,
            }).finally(() => {
                setTableLoading(false)
            })
            console.log('getProjectInfoList: ', res)
            const content = res.content as ILogResData[]
            setDataSource([...content])
            setPaginationInfo({
                pageIndex: params?.pageIndex || paginationInfo.pageIndex,
                pageSize: params?.pageSize || paginationInfo.pageSize,
                total: res.totalElements
            })
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    }
    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/',
            breadcrumbName: t('View Log List All'),
        },
    ]

    const defaultColumns: (ColumnTypes[number] & IColProperty)[] = [
        {
            title: () => <span>{t('PTN')}</span>,
            dataIndex: 'techPtn',
            key: 'techPtn',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
        },
        {
            title: () => <span>{t('modifyTime')}</span>,
            dataIndex: 'createTime',
            key: 'createTime',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
            render (value) {
                return moment(value).format('YYYY-MM-DD HH:ss:mm')
            }
        },
        {
            title: () => <span>{t('updator')}</span>,
            dataIndex: 'creator',
            key: 'creator',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
        },
        {
            title: () => <span>{t('Action')}</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 150,
            fixed: 'right',
            render: viewButton
        }
    ]

    // 分页
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber: number): void => {
        if (pageSizeChanged.current) return
        getList({
            ...paginationInfo,
            pageIndex: pageNumber
        })
    }
    const onPageSizeChange = (current: number, size: number) => {
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        getList({
            ...paginationInfo,
            pageIndex: current,
            pageSize: size
        })

        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }
    useEffect(() => {
        console.log('history.location: ', history.location, locationObj, route, workgroupList)
        setTableKey(getUuid(8))

        if (workgroupList.length === 0) {
            dispatch(getWorkgroup())
        }

        setInit(false)
        
        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => {})
        }

        getList()

        const offsetH = 282
        setWinh(document.body.clientHeight - offsetH)
        const onResize = () => {
            setWinh(document.body.clientHeight - offsetH)
        }
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [])
    useEffect(() => {
        console.log('init: ', init)
    }, [init])
    useEffect(() => {
        if (didMountRef.current) {
            console.log('didMountRef: ', didMountRef.current)
        } else {
            didMountRef.current = true
        }
    }, [searchObj])
    const onChangeInput = (e:any)=>{
        console.log(e)
        setSearchObj({
            ...searchObj,
            techPtn: e.target.value
        })
    }
    return (
        <DataSourceContext.Provider value={{
            dataSource,
            setDataSource
        }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Nav dispatch={dispatch} />
                <PageBreadcrumb routerParam={routes}
                    history={history}
                    route={route}
                    dispatch={dispatch}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PageHeader className='chart_title2'
                        onBack={() => null}
                        title=''
                    />
                    <Space>
                        <Input allowClear placeholder={t('PTN Placeholder')} onChange={onChangeInput} onKeyUp={(e: any) => {
                            if (e.code === 'Enter') {
                                getList()
                            }
                        }} />
                        <Button type='primary'
                            icon={<SearchOutlined />}
                            onClick={() => {
                                getList()
                            }}
                        >{t('search')}</Button>
                    </Space>
                </div>
                <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
                    <Table className='index_ls' bordered
                        key={tableKey}
                        dataSource={dataSource}
                        components={components}
                        tableLayout='fixed'
                        columns={defaultColumns}
                        rowKey="id"
                        scroll={{ x: 'max-content', y: winH }}
                        loading={tableLoading}
                        pagination={{
                            current: paginationInfo.pageIndex,
                            pageSize: paginationInfo.pageSize,
                            total: paginationInfo.total,
                            showQuickJumper: true,
                            onChange: onPaginationChange,
                            showSizeChanger: true,
                            onShowSizeChange: onPageSizeChange,
                            showTotal: (total) => 
                                `${t('total')} ${total} ${t('items')}`,
                            style: { paddingBottom: 10 }
                        }}
                    />
                </ConfigProvider>
                <LogViewDetailPop show={showLogDetailPop} hide={() => {
                    setShowLogDetailPop(false)
                }} recordId={viewHistoryId} record={viewHistoryRecord} />
            </Space>
        </DataSourceContext.Provider>
    )
}

export default TablePage2
